import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminLayout from '../../components/AdminLayout';
import { stripHtml } from '../../utils/html';

// ========== 样式组件 ==========
const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1rem;
`;
const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--gray-900);
`;
const Form = styled.form`
  background: var(--white);
  padding: 2rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow-sm);
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;
const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--gray-700);
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  background-color: white;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  input { width: 18px; height: 18px; cursor: pointer; }
`;
const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background-color: var(--primary-light);
  color: white;
  border-radius: var(--border-radius-full);
  font-size: 0.875rem;
`;
const TagInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  width: 160px;
  &:focus { outline: none; border-color: var(--primary-color); }
`;
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  &:hover { opacity: 0.9; }
  &:disabled { background-color: var(--gray-400); cursor: not-allowed; }
`;
const CancelButton = styled(Button)`
  background-color: var(--gray-200);
  color: var(--gray-700);
  margin-right: 1rem;
  &:hover { background-color: var(--gray-300); }
`;
const QuillWrapper = styled.div`
  .ql-toolbar {
    border-radius: 6px 6px 0 0;
    border-color: var(--gray-300);
    background: #fafafa;
  }
  .ql-container {
    border-radius: 0 0 6px 6px;
    border-color: var(--gray-300);
    min-height: 280px;
    font-size: 1rem;
  }
  .ql-editor {
    min-height: 280px;
  }
`;
const SmallButton = styled.button`
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-sm);
  background: var(--white);
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { background: var(--gray-100); }
`;
const ErrorMessage = styled.div`
  color: var(--danger-color);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;
const SuccessMessage = styled.div`
  color: var(--success-color);
  font-size: 0.875rem;
  padding: 0.75rem;
  background-color: var(--success-light);
  border-radius: var(--border-radius-md);
  margin-bottom: 1rem;
`;
const SubTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--gray-800);
  margin: 2rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary-light);
`;
const SectionBlock = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: ${props => props.$bg || '#f8f9fa'};
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

// 图片管理样式
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
`;
const ImageCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--gray-200)'};
`;
const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const ImageDeleteBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.85);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #dc3545; }
`;
const UploadArea = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  border: 2px dashed var(--gray-300);
  border-radius: 8px;
  cursor: pointer;
  color: var(--gray-500);
  transition: all 0.2s;
  &:hover { border-color: var(--primary-color); color: var(--primary-color); }
  input { display: none; }
`;

// AI 填充样式
const AISection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: var(--border-radius-lg);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  color: white;
`;
const AISectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
`;
const AIToggleButton = styled.button`
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 0.25rem 1rem;
  border-radius: var(--border-radius-full);
  cursor: pointer;
  font-size: 0.8rem;
  &:hover { background: rgba(255,255,255,0.3); }
`;
const AIBody = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,0.2);
`;
const AITextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  background: rgba(255,255,255,0.1);
  color: white;
  resize: vertical;
  min-height: 100px;
  &::placeholder { color: rgba(255,255,255,0.6); }
  &:focus { outline: none; border-color: rgba(255,255,255,0.6); }
`;
const AIButtonRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
`;
const AIConvertButton = styled.button`
  padding: 0.6rem 1.5rem;
  background: white;
  color: #667eea;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f0f0ff; }
  &:disabled { background: rgba(255,255,255,0.3); color: rgba(255,255,255,0.8); cursor: not-allowed; }
`;

// ========== 常量 ==========
const PRODUCT_CATEGORIES = [
  '软件产品', '硬件设备', '智能硬件', '物联网设备', '通信设备',
  '安防设备', '系统软件', '应用软件', '平台服务', '数据服务'
];
const STATUS_OPTIONS = [
  { value: 'active', label: '已发布（前台可见）' },
  { value: 'draft', label: '草稿（不公开）' },
  { value: 'inactive', label: '已下架（不公开）' }
];

const emptyProduct = () => ({
  name: '',
  description: '',
  detailedDescription: '',
  price: 0,
  category: '软件产品',
  subCategory: '',
  images: [],
  features: [],
  specifications: {},
  applicationScenarios: [],
  advantages: [],
  inStock: true,
  stockQuantity: 999,
  tags: [],
  status: 'active',
  categoryPath: [],
  commissionRate: 0
});

// 当前登录者是否为供应商
const getIsSupplier = () => {
  try {
    return JSON.parse(localStorage.getItem('adminUser') || '{}').role === 'supplier';
  } catch (e) {
    return false;
  }
};

// ========== 组件 ==========
const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef(null);

  // 供应商：只能维护自己上传的产品，且提交后需我司审核
  const isSupplier = getIsSupplier();
  const productsBaseUrl = isSupplier ? '/api/supplier/products' : '/api/admin/products';

  console.log('AdminProductForm 渲染, id:', id, 'isEditMode:', isEditMode);

  const [product, setProduct] = useState(emptyProduct());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 动态字段
  const [newTag, setNewTag] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newScenario, setNewScenario] = useState('');
  const [newAdvTitle, setNewAdvTitle] = useState('');
  const [newAdvDesc, setNewAdvDesc] = useState('');
  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');
  const [newCategoryPath, setNewCategoryPath] = useState('');

  // 图片
  const [uploadingImages, setUploadingImages] = useState(false);
  const [removedImages, setRemovedImages] = useState([]);

  // AI
  const [showAI, setShowAI] = useState(true);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState('');

  // ========== 加载编辑数据 ==========
  useEffect(() => {
    if (id) fetchProduct();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${productsBaseUrl}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.product) {
        const p = response.data.product;
        setProduct({
          name: p.name || '',
          // 历史数据里可能已存了富文本，载入编辑器前先转成纯文本
          description: stripHtml(p.description),
          detailedDescription: stripHtml(p.detailedDescription),
          price: p.price || 0,
          category: p.category || '软件产品',
          subCategory: p.subCategory || '',
          images: p.images || [],
          features: p.features || [],
          specifications: p.specifications || {},
          applicationScenarios: p.applicationScenarios || [],
          advantages: p.advantages || [],
          inStock: p.inStock !== undefined ? p.inStock : true,
          stockQuantity: p.stockQuantity ?? 999,
          tags: p.tags || [],
          status: p.status || 'active',
          categoryPath: p.categoryPath || [],
          commissionRate: p.commissionRate ?? 0,
          auditStatus: p.auditStatus || 'approved',
          audit: p.audit || null
        });
      }
    } catch (err) {
      setError('获取产品信息失败');
      console.error('获取产品失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========== 基础处理 ==========
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ========== 图片管理 ==========
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (const file of files) formData.append('images', file);

    try {
      setUploadingImages(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${productsBaseUrl}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.urls) {
        setProduct(prev => ({ ...prev, images: [...prev.images, ...res.data.urls] }));
      }
    } catch (err) {
      console.error('上传图片失败:', err);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (imgUrl, index) => {
    setRemovedImages(prev => [...prev, imgUrl]);
    setProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // ========== Tag/Array 字段助手 ==========
  const addItem = (field, value, setter) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const arr = Array.isArray(product[field]) ? product[field] : [];
    if (!arr.includes(trimmed)) {
      setProduct(prev => ({ ...prev, [field]: [...arr, trimmed] }));
    }
    setter('');
  };

  const removeItem = (field, index) => {
    const arr = Array.isArray(product[field]) ? product[field] : [];
    setProduct(prev => ({
      ...prev,
      [field]: arr.filter((_, i) => i !== index)
    }));
  };

  // ========== AI 填充 ==========
  const handleAIConvert = async () => {
    if (aiInput.trim().length < 20) return;
    setAiLoading(true);
    setAiMessage('');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/ai/convert-to-product', { text: aiInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setProduct(prev => ({
          ...prev,
          name: d.name || prev.name,
          description: d.description ? stripHtml(d.description) : prev.description,
          category: d.category || prev.category,
          detailedDescription: stripHtml(d.detailedDescription),
          features: d.features || [],
          specifications: d.specifications || {},
          applicationScenarios: d.applicationScenarios || [],
          advantages: d.advantages || [],
          subCategory: d.subCategory || '',
          categoryPath: d.categoryPath || [],
          price: d.price || 0,
          inStock: d.inStock !== undefined ? d.inStock : true,
          stockQuantity: d.stockQuantity || 999
        }));
        setAiMessage('✅ AI填充成功！请检查并修改后提交。');
      }
    } catch (err) {
      setAiMessage('❌ AI转换失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setAiLoading(false);
    }
  };

  // ========== 规格参数 ==========
  const addSpec = () => {
    if (!specKey.trim()) return;
    setProduct(prev => ({
      ...prev,
      specifications: { ...(prev.specifications || {}), [specKey.trim()]: specValue.trim() }
    }));
    setSpecKey('');
    setSpecValue('');
  };
  const removeSpec = (key) => {
    setProduct(prev => {
      const newSpecs = { ...(prev.specifications || {}) };
      delete newSpecs[key];
      return { ...prev, specifications: newSpecs };
    });
  };

  // ========== 优势 ==========
  const addAdvantage = () => {
    if (!newAdvTitle.trim()) return;
    setProduct(prev => ({
      ...prev,
      advantages: [...(prev.advantages || []), { title: newAdvTitle.trim(), description: newAdvDesc.trim() }]
    }));
    setNewAdvTitle('');
    setNewAdvDesc('');
  };

  // ========== 提交 ==========
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // 富文本编辑器（Quill）输出必然带 <p></p> 等标签，保存前统一转成纯文本
    const payload = {
      ...product,
      description: stripHtml(product.description),
      detailedDescription: stripHtml(product.detailedDescription),
      removedImages
    };
    const token = localStorage.getItem('adminToken');
    try {
      setLoading(true);
      if (isEditMode) {
        await axios.put(`${productsBaseUrl}/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(isSupplier ? '修改已提交，等待我司审核通过后生效！' : '产品更新成功！');
      } else {
        await axios.post(productsBaseUrl, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(isSupplier ? '产品已提交，等待我司审核通过后展示！' : '产品创建成功！');
      }
      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || '保存产品失败');
      console.error('保存产品失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========== 键位回车 ==========
  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };

  if (loading && isEditMode && !product.name) {
    return <AdminLayout title={isEditMode ? '编辑产品' : '添加产品'}><Container><SectionTitle>加载中...</SectionTitle></Container></AdminLayout>;
  }

  return (
    <AdminLayout title={isEditMode ? '编辑产品' : '添加产品'}>
      <Container>
        <SectionTitle>{isEditMode ? '编辑产品' : '添加产品'}</SectionTitle>

        {/* AI 智能填充 */}
        {!isEditMode && (
          <AISection>
            <AISectionHeader>
              <span>🤖 AI 智能填充</span>
              <AIToggleButton onClick={() => setShowAI(!showAI)}>{showAI ? '收起' : '展开'}</AIToggleButton>
            </AISectionHeader>
            {showAI && (
              <AIBody>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.85)', marginBottom: '0.75rem' }}>
                  粘贴产品宣传文本或PDF内容，AI将自动识别并填充下方表单。
                </p>
                <AITextarea value={aiInput} onChange={e => setAiInput(e.target.value)} rows={6}
                  placeholder="请在此粘贴产品介绍文本（至少20个字符）..." />
                <AIButtonRow>
                  <AIConvertButton onClick={handleAIConvert} disabled={aiLoading || aiInput.trim().length < 20}>
                    {aiLoading ? '🔄 AI分析中...' : '✨ 智能填充表单'}
                  </AIConvertButton>
                  {aiMessage && <span style={{ fontSize: '0.8rem', marginLeft: '1rem' }}>{aiMessage}</span>}
                </AIButtonRow>
              </AIBody>
            )}
          </AISection>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          {isSupplier && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#fff3cd',
              color: '#856404',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              您提交的新增或修改需经我司管理人员审核通过后才会展示。已展示的产品被修改时，线上内容在审核通过前保持不变。
            </div>
          )}

          {isEditMode && product.auditStatus === 'rejected' && product.audit?.remark && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              上次审核被驳回：{product.audit.remark}
            </div>
          )}

          {isEditMode && (product.auditStatus === 'pending' || product.pendingData) && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              background: '#e7f3ff',
              color: '#004085',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              当前有内容正在等待我司审核。
            </div>
          )}

          {/* ---- 基本信息 ---- */}
          <SubTitle>📋 基本信息</SubTitle>
          <FormRow>
            <FormGroup>
              <Label>产品名称 *</Label>
              <Input name="name" value={product.name} onChange={handleChange} required placeholder="请输入产品名称" />
            </FormGroup>
            <FormGroup>
              <Label>价格 (元)</Label>
              <Input name="price" type="number" value={product.price} onChange={handleChange} min="0" step="0.01" />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>产品分类 *</Label>
              <Select name="category" value={product.category} onChange={handleChange} required>
                {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>子分类</Label>
              <Input name="subCategory" value={product.subCategory} onChange={handleChange} placeholder="如：VoIP网关、呼叫中心软件" />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>库存数量</Label>
              <Input name="stockQuantity" type="number" value={product.stockQuantity} onChange={handleChange} min="0" />
            </FormGroup>
            <FormGroup>
              <Label>分成比（给我司，%）</Label>
              <Input
                name="commissionRate"
                type="number"
                value={product.commissionRate ?? 0}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.1"
                placeholder="如 15 表示销售额的 15% 归我司"
              />
            </FormGroup>
          </FormRow>
          {/* 上下架由我司管理人员控制，供应商不显示 */}
          {!isSupplier && (
            <FormRow>
              <FormGroup>
                <Label>发布状态 *</Label>
                <Select name="status" value={product.status} onChange={handleChange}>
                  {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              </FormGroup>
            </FormRow>
          )}

          {/* ---- 描述信息 ---- */}
          <SubTitle>📝 描述信息</SubTitle>
          <FormGroupFull>
            <Label>简短描述（保存为纯文本，自动去除富文本标签）*</Label>
            <QuillWrapper>
              <ReactQuill
                value={product.description}
                onChange={(content) => setProduct(prev => ({ ...prev, description: content }))}
                placeholder="用于列表展示的简短描述"
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'clean']
                  ]
                }}
                formats={['bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'link']}
              />
            </QuillWrapper>
          </FormGroupFull>
          <FormGroupFull>
            <Label>详细描述（保存为纯文本，自动去除富文本标签）</Label>
            <QuillWrapper>
              <ReactQuill
                value={product.detailedDescription}
                onChange={(content) => setProduct(prev => ({ ...prev, detailedDescription: content }))}
                placeholder="产品的完整介绍（支持富文本编辑：加粗、斜体、列表、图片、链接等）"
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['blockquote', 'code-block'],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header', 'bold', 'italic', 'underline', 'strike',
                  'color', 'background', 'list', 'bullet', 'align',
                  'blockquote', 'code-block', 'link', 'image'
                ]}
              />
            </QuillWrapper>
          </FormGroupFull>

          {/* ---- 特性、场景、优势 ---- */}
          <SubTitle>✨ 产品特性与亮点</SubTitle>
          <FormRow>
            <FormGroup>
              <Label>产品特性/功能点</Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newFeature} onChange={e => setNewFeature(e.target.value)}
                  onKeyDown={onEnter(() => addItem('features', newFeature, setNewFeature))}
                  placeholder="输入特性后按回车" />
                <SmallButton type="button" onClick={() => addItem('features', newFeature, setNewFeature)}>添加</SmallButton>
              </div>
              {(product.features || []).length > 0 && (
                <TagContainer>
                  {product.features.map((f, i) => (
                    <Tag key={i}>{f}
                      <span onClick={() => removeItem('features', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                    </Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
            <FormGroup>
              <Label>应用场景</Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newScenario} onChange={e => setNewScenario(e.target.value)}
                  onKeyDown={onEnter(() => addItem('applicationScenarios', newScenario, setNewScenario))}
                  placeholder="输入场景后按回车" />
                <SmallButton type="button" onClick={() => addItem('applicationScenarios', newScenario, setNewScenario)}>添加</SmallButton>
              </div>
              {(product.applicationScenarios || []).length > 0 && (
                <TagContainer>
                  {product.applicationScenarios.map((s, i) => (
                    <Tag key={i} style={{ background: 'var(--success-color)' }}>{s}
                      <span onClick={() => removeItem('applicationScenarios', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                    </Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
          </FormRow>

          {/* ---- 分类路径 ---- */}
          <FormGroup>
            <Label>分类路径（用于前台导航面包屑）</Label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <TagInput value={newCategoryPath} onChange={e => setNewCategoryPath(e.target.value)}
                onKeyDown={onEnter(() => addItem('categoryPath', newCategoryPath, setNewCategoryPath))}
                placeholder="输入后按回车" />
              <SmallButton type="button" onClick={() => addItem('categoryPath', newCategoryPath, setNewCategoryPath)}>添加</SmallButton>
            </div>
            {(product.categoryPath || []).length > 0 && (
              <TagContainer>
                {product.categoryPath.map((p, i) => (
                  <Tag key={i} style={{ background: '#f59e0b' }}>{p}
                    <span onClick={() => removeItem('categoryPath', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>

          {/* ---- 优势 ---- */}
          <SectionBlock>
            <h4 style={{ margin: '0 0 1rem', color: 'var(--gray-700)' }}>🏆 产品优势</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'start', marginBottom: '0.75rem' }}>
              <Input value={newAdvTitle} onChange={e => setNewAdvTitle(e.target.value)} placeholder="优势标题" />
              <Input value={newAdvDesc} onChange={e => setNewAdvDesc(e.target.value)} placeholder="优势描述（可选）" />
              <Button type="button" onClick={addAdvantage} style={{ padding: '0.75rem 1rem' }}>添加</Button>
            </div>
            {(product.advantages || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {product.advantages.map((adv, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                    <div>
                      <strong style={{ color: 'var(--primary-color)' }}>{adv.title}</strong>
                      {adv.description && <span style={{ color: '#6c757d', marginLeft: '0.75rem', fontSize: '0.9rem' }}>- {adv.description}</span>}
                    </div>
                    <span onClick={() => removeItem('advantages', i)} style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}>×</span>
                  </div>
                ))}
              </div>
            )}
          </SectionBlock>

          {/* ---- 规格参数 ---- */}
          <SectionBlock $bg="#fff">
            <h4 style={{ margin: '0 0 1rem', color: 'var(--gray-700)' }}>📐 规格参数</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'start', marginBottom: '0.75rem' }}>
              <Input value={specKey} onChange={e => setSpecKey(e.target.value)} placeholder="参数名（如：CPU）" onKeyDown={onEnter(addSpec)} />
              <Input value={specValue} onChange={e => setSpecValue(e.target.value)} placeholder="参数值（如：Intel i7）" onKeyDown={onEnter(addSpec)} />
              <Button type="button" onClick={addSpec} style={{ padding: '0.75rem 1rem' }}>添加</Button>
            </div>
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.4rem' }}>
                {Object.entries(product.specifications).map(([key, val]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.75rem', background: '#f8f9fa', borderRadius: '6px', fontSize: '0.9rem' }}>
                    <span><strong>{key}:</strong> {val}</span>
                    <span onClick={() => removeSpec(key)} style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}>×</span>
                  </div>
                ))}
              </div>
            )}
          </SectionBlock>

          {/* ---- 图片管理 ---- */}
          <SubTitle>🖼️ 产品图片</SubTitle>
          {product.images.length > 0 && (
            <ImageGrid>
              {product.images.map((img, i) => (
                <ImageCard key={i} $active>
                  <ImagePreview src={img} alt={`product-${i}`} onError={(e) => { e.target.style.display = 'none'; }} />
                  <ImageDeleteBtn type="button" onClick={() => handleRemoveImage(img, i)}>×</ImageDeleteBtn>
                </ImageCard>
              ))}
            </ImageGrid>
          )}
          <UploadArea style={{ marginTop: '0.75rem' }}>
            <input type="file" multiple accept="image/*" ref={fileInputRef} onChange={handleImageUpload} />
            <span style={{ fontSize: '1.5rem' }}>📁</span>
            <span>{uploadingImages ? '上传中...' : '点击上传图片（支持多选，单张≤5MB）'}</span>
          </UploadArea>

          {/* ---- 库存与标签 ---- */}
          <FormRow style={{ marginTop: '1.5rem' }}>
            <FormGroup>
              <CheckboxLabel>
                <input type="checkbox" name="inStock" checked={product.inStock} onChange={handleChange} />
                有库存
              </CheckboxLabel>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>产品标签</Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <TagInput value={newTag} onChange={e => setNewTag(e.target.value)}
                onKeyDown={onEnter(() => addItem('tags', newTag, setNewTag))} placeholder="输入标签后按回车" />
              <SmallButton type="button" onClick={() => addItem('tags', newTag, setNewTag)}>添加</SmallButton>
            </div>
            {product.tags.length > 0 && (
              <TagContainer>
                {product.tags.map((tag, i) => (
                  <Tag key={i}>{tag}
                    <span onClick={() => removeItem('tags', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>

          {/* ---- 按钮 ---- */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <CancelButton type="button" onClick={() => navigate('/admin/products')}>取消</CancelButton>
            <Button type="submit" disabled={loading}>
              {loading ? '保存中...' : (isEditMode ? '💾 更新产品' : '➕ 添加产品')}
            </Button>
          </div>
        </Form>
      </Container>
    </AdminLayout>
  );
};

export default AdminProductForm;
